import React, { useMemo, useCallback } from 'react';

import { Box, Flex, Text } from 'rebass/styled-components';
import { CheckCircle, AlertTriangle } from 'lucide-react';
import { Button } from '@getro/rombo';
import PropTypes from 'prop-types';
import { AddCompanyProcess } from 'components/atoms/AddCompanyProcess';

const CompaniesAdded = ({ meta, onDone }) => {
  const { created, published, failed, notActive } = meta;

  const organizationsCreated = created + published;
  const showOnlyError = !created && !published && (failed.length || notActive.length);

  const formatDomainList = useCallback((list) => {
    const { length } = list;

    if (length === 1) {
      return list[0];
    }

    return list
      .reduce((acc, value, index) => {
        if (index === length - 1) {
          acc.push('and');
          acc.push(value);

          return acc;
        }

        acc.push(`${value},`);

        return acc;
      }, [])
      .join(' ');
  }, []);

  const formatFailed = useMemo(() => formatDomainList(failed.map(({ domain }) => domain)), [failed, formatDomainList]);
  const formatNotActive = useMemo(() => formatDomainList(notActive), [notActive, formatDomainList]);

  return (
    <Box maxWidth={460} m="64px auto">
      <Flex color={showOnlyError ? 'orange' : 'success'} alignItems="center">
        <Box
          width="64px"
          height="64px"
          strokeWidth="1px"
          as={showOnlyError ? AlertTriangle : CheckCircle}
          aria-hidden="true"
        />
      </Flex>

      <Text mt={32} mb={16} as="h1" color="text.dark" fontWeight="600" fontSize={18}>
        {organizationsCreated > 0 ? organizationsCreated : 'No'}{' '}
        {`${organizationsCreated === 1 ? 'company' : 'companies'} added`}
      </Text>

      <Flex mt={3} flexDirection="column" sx={{ rowGap: '16px' }}>
        {(published > 0 || created.length > 0 || failed.length > 0 || notActive.length > 0) && (
          <Flex flexDirection="column" pl={24} as="ul" fontSize={16} sx={{ rowGap: '8px' }}>
            {published > 0 && (
              <Box as="li">
                {published} {published === 1 ? 'is' : 'are'} already visible on your job board.
              </Box>
            )}

            {created.length > 0 && (
              <Box as="li">
                {created.length} not published because we don&apos;t have info: our team will add info and jobs, and
                publish them. <AddCompanyProcess />
              </Box>
            )}
            {failed.length > 0 && (
              <Box as="li">
                {failed.length} {failed.length === 1 ? 'domain was' : 'domains were'} not added because they&apos;re not
                hosting a website: {formatFailed}.
              </Box>
            )}
            {notActive.length > 0 && (
              <Box as="li">
                {notActive.length} could not be added: {formatNotActive}. Contact us at{' '}
                <a href="mailto:help@getro.com">help@getro.com</a>.
              </Box>
            )}
          </Flex>
        )}
        {!showOnlyError && (
          <Text color="tex.main" as="p" fontSize={16}>
            We will notify when information and jobs are updated, it usually takes less than 2 business days.
          </Text>
        )}
      </Flex>
      <Button onClick={onDone} width={70} height={40} mt={4} href="#" variant="primary">
        Ok
      </Button>
    </Box>
  );
};

CompaniesAdded.propTypes = {
  meta: PropTypes.objectOf({
    created: PropTypes.number.isRequired,
    failed: PropTypes.arrayOf(
      PropTypes.shape({
        domain: PropTypes.string.isRequired,
        error: PropTypes.string,
      }),
    ).isRequired,
    published: PropTypes.number.isRequired,
    notActive: PropTypes.arrayOf(PropTypes.string).isRequired,
  }).isRequired,

  onDone: PropTypes.func.isRequired,
};

export default CompaniesAdded;
